// ============================================================
// CalendarMonthGrid.tsx
// Stage 2 of the Calendar/Diary rebuild — the read-only month grid
// in the "B in Casevo" look, built entirely from the app's existing
// Casevo Tailwind tokens (no raw hex).
//
// LOOK (locked):
//   - DM Sans (inherited via font-sans on the page shell).
//   - Mon-first, dense 7-column grid. Cells are bg-casevo-offwhite
//     with a subtle border-casevo-border.
//   - TODAY's cell gets the mint focus-ring: border-casevo-brand +
//     a casevo-mint ring so the current day pops without shouting.
//   - TASK chips: soft green fill (bg-casevo-mint / forest text) —
//     the same family as the app's existing status pills.
//   - DEADLINE chips: the one deliberate non-green accent — a soft
//     red fill + a red left-border so risk is visible at a glance.
//   - Up to 2 items per cell, then a "+N more" overflow line.
//   - A 🗒 pin sits in the day header when a day-note exists.
//
// This stage is READ-ONLY: chips and cells are not interactive, there
// is no click-day panel and no create affordances (that is Stage 3),
// and it renders in light mode only (dark focus-mode is Stage 4).
//
// DATE KEYING: the grid works in "civil" calendar days. Cell dates are
// built with the LOCAL Date constructor (so getDate() is the civil
// day), while entries + day-notes — which the backend stores date-only
// at midnight UTC — are keyed by their UTC parts. Both yield the same
// YYYY-MM-DD civil key, so a date-only deadline lands on its intended
// cell regardless of the viewer's timezone. This mirrors the day-
// semantics the old grouped list already used (getUTCDate()).
// ============================================================
import { CalMarker, IcBell, IcNote } from './calendarIcons'
import React, { useMemo } from 'react'
import { CalendarEntry, DayNote, Reminder } from '../../types/calendar.types'
import { entryDayKey, reminderDayKey } from './CalendarTimeGrid'
import { useLiveUserName } from '../../hooks/useLiveUserName'
import { SkeletonBar, fadeClassFor, type CalendarFade } from './CalendarSkeleton'

// Mon-first weekday labels for the column header row.
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

// Zero-padded civil key builder — YYYY-MM-DD from integer parts.
const civilKey = (y: number, m0: number, d: number): string =>
  `${y}-${String(m0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`

// Key an entry/day-note by its UTC calendar day. Date-only rows arrive
// as midnight UTC, so the UTC parts ARE the semantic civil day.
const utcKey = (iso: string): string => {
  const d = new Date(iso)
  return civilKey(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

// Key a cell (built with the local Date constructor) by its local parts.
const localKey = (d: Date): string => civilKey(d.getFullYear(), d.getMonth(), d.getDate())

interface CellData {
  date: Date
  key: string
  inMonth: boolean
  isToday: boolean
  // Split so deadlines (risk) can render first / distinctly.
  deadlines: CalendarEntry[]
  tasks: CalendarEntry[]
  hasNote: boolean
  hasReminder: boolean
}

interface CalendarMonthGridProps {
  monthDate: Date // any date within the month to display
  // The range's data isn't ready yet → every cell shows a skeleton (the grid
  // itself, dates and navigation are live straight away).
  loading?: boolean
  // Which items fade in when they mount (see CalendarSkeleton.fadeClassFor).
  fade?: CalendarFade
  // Small status shown in the header row (e.g. "Loading Outlook…").
  statusSlot?: React.ReactNode
  entries: CalendarEntry[]
  dayNotes: DayNote[]
  reminders: Reminder[]
  onPrevMonth: () => void
  onNextMonth: () => void
  onToday: () => void
  // Clicking a cell's BODY opens the day detail panel. Passed the cell's
  // Date (built at local midnight for that civil day).
  onDayClick: (date: Date) => void
  // Clicking a cell's day NUMBER switches to Day view for that date
  // (distinct from the panel, so the two don't clash).
  onViewDay: (date: Date) => void
  // Right-click a cell → the add-menu (hour is always null in month view).
  onContextMenu: (e: React.MouseEvent, date: Date, hour: number | null) => void
}

const CalendarMonthGrid: React.FC<CalendarMonthGridProps> = ({
  monthDate,
  loading = false,
  fade = 'none',
  statusSlot,
  entries,
  dayNotes,
  reminders,
  onPrevMonth,
  onNextMonth,
  onToday,
  onDayClick,
  onViewDay,
  onContextMenu,
}) => {
  const year = monthDate.getFullYear()
  const month = monthDate.getMonth() // 0-indexed

  // Index entries + notes + reminders by civil day for O(1) cell lookups.
  const { entriesByDay, notedDays, reminderDays } = useMemo(() => {
    const map = new Map<string, { deadlines: CalendarEntry[]; tasks: CalendarEntry[] }>()
    for (const entry of entries) {
      // Calendar is forward-looking; a task marked done drops off the
      // grid (it lives on the Tasks list). Case deadlines always stay.
      if (entry.type === 'task' && entry.status === 'done') continue
      // Timed items on their LOCAL day (My Work's rule) — see entryDayKey.
      const key = entryDayKey(entry)
      let bucket = map.get(key)
      if (!bucket) {
        bucket = { deadlines: [], tasks: [] }
        map.set(key, bucket)
      }
      if (entry.type === 'case_deadline') bucket.deadlines.push(entry)
      else bucket.tasks.push(entry)
    }
    const noted = new Set<string>()
    for (const note of dayNotes) noted.add(utcKey(note.date))
    const reminded = new Set<string>()
    for (const r of reminders) reminded.add(reminderDayKey(r))
    return { entriesByDay: map, notedDays: noted, reminderDays: reminded }
  }, [entries, dayNotes, reminders])

  // Build the Mon-first grid cells for the viewed month.
  const cells = useMemo<CellData[]>(() => {
    const todayKey = localKey(new Date())
    const first = new Date(year, month, 1)
    const leading = (first.getDay() + 6) % 7 // Mon=0 offset
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    // Round up to whole weeks so the grid has no ragged final row.
    const totalCells = Math.ceil((leading + daysInMonth) / 7) * 7
    const out: CellData[] = []
    for (let i = 0; i < totalCells; i++) {
      // Day arithmetic on the local constructor handles month rollover
      // for the leading (prev-month) and trailing (next-month) cells.
      const date = new Date(year, month, 1 - leading + i)
      const key = localKey(date)
      const bucket = entriesByDay.get(key)
      out.push({
        date,
        key,
        inMonth: date.getMonth() === month,
        isToday: key === todayKey,
        deadlines: bucket?.deadlines ?? [],
        tasks: bucket?.tasks ?? [],
        hasNote: notedDays.has(key),
        hasReminder: reminderDays.has(key),
      })
    }
    return out
  }, [year, month, entriesByDay, notedDays, reminderDays])

  const monthLabel = monthDate.toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
  })

  return (
    <div>
      {/* ── Month navigation header ─────────────────────────── */}
      {/* Wraps at phone width: the ‹ Today › cluster drops under the title
          rather than pushing the page wider. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mb-4" data-calendar-nav="">
        <h2 className="min-w-0 text-lg sm:text-xl font-medium text-casevo-forest">{monthLabel}</h2>
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {statusSlot}
          <button
            type="button"
            onClick={onToday}
            className="text-xs uppercase tracking-widest text-casevo-brand border border-casevo-border rounded-md px-3 py-1.5 hover:border-casevo-mid hover:bg-casevo-mint/30 transition-colors focus:outline-none focus-visible:border-casevo-mid"
          >
            Today
          </button>
          <button
            type="button"
            onClick={onPrevMonth}
            aria-label="Previous month"
            className="w-8 h-8 flex items-center justify-center text-casevo-mid border border-casevo-border rounded-md hover:border-casevo-mid hover:bg-casevo-mint/30 hover:text-casevo-forest transition-colors focus:outline-none focus-visible:border-casevo-mid"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={onNextMonth}
            aria-label="Next month"
            className="w-8 h-8 flex items-center justify-center text-casevo-mid border border-casevo-border rounded-md hover:border-casevo-mid hover:bg-casevo-mint/30 hover:text-casevo-forest transition-colors focus:outline-none focus-visible:border-casevo-mid"
          >
            ›
          </button>
        </div>
      </div>

      {/* ── Weekday header row ───────────────────────────────── */}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5 mb-1">
        {WEEKDAYS.map((label) => (
          <div
            key={label}
            className="text-[10px] sm:text-xs uppercase tracking-widest text-casevo-mid font-medium text-center py-1"
          >
            {label}
          </div>
        ))}
      </div>

      {/* ── Day cells ────────────────────────────────────────── */}
      {loading && <span role="status" className="sr-only">Loading calendar…</span>}
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {cells.map((cell, i) => (
          <DayCell
            key={cell.key}
            cell={cell}
            loading={loading}
            index={i}
            fade={fade}
            onClick={() => onDayClick(cell.date)}
            onViewDay={() => onViewDay(cell.date)}
            onContextMenu={(e) => onContextMenu(e, cell.date, null)}
          />
        ))}
      </div>
    </div>
  )
}

// ── DAY CELL ───────────────────────────────────────────────
// A single grid square. Combines deadlines (rendered first, red) and
// tasks (green) into one ordered list, shows the first two, and
// collapses the rest into a "+N more" line. Adjacent-month days are
// dimmed so the current month reads as the focus.
const DayCell: React.FC<{
  cell: CellData
  loading: boolean
  index: number
  fade: CalendarFade
  onClick: () => void
  onViewDay: () => void
  onContextMenu: (e: React.MouseEvent) => void
}> = ({ cell, loading, index, fade, onClick, onViewDay, onContextMenu }) => {
  // Deadlines first so risk is always visible even when a busy day
  // overflows; tasks fill the remaining slots.
  const items: CalendarEntry[] = [...cell.deadlines, ...cell.tasks]
  const MAX = 2
  const shown = items.slice(0, MAX)
  const overflow = items.length - shown.length

  const base =
    'min-h-[92px] sm:min-h-[120px] rounded-lg border p-2 flex flex-col bg-casevo-offwhite text-left transition-colors cursor-pointer hover:border-casevo-mid focus:outline-none focus-visible:border-casevo-mid'
  // Today's mint ring. In dark focus mode casevo-mint is a dark tint
  // (invisible on the dark ground), so brighten the ring to casevo-light.
  const ring = cell.isToday
    ? 'border-casevo-brand ring-2 ring-casevo-mint dark:ring-casevo-light'
    : 'border-casevo-border'
  const dim = cell.inMonth ? '' : 'opacity-45'

  // The cell BODY opens the day panel (onClick); the day NUMBER opens Day
  // view (onViewDay). A div-role-button hosts a nested real <button> for the
  // number (a button-in-button would be invalid HTML). Right-click → menu.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      onContextMenu={onContextMenu}
      className={`${base} ${ring} ${dim}`}
    >
      {/* Day header: number (→ Day view) + optional note pin. */}
      <div className="flex items-center justify-between mb-1">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onViewDay() }}
          title="Open day view"
          className={`text-xs font-medium rounded px-1 -mx-1 hover:bg-casevo-mint/40 focus:outline-none focus-visible:bg-casevo-mint/40 ${
            cell.isToday ? 'text-casevo-brand' : 'text-casevo-forest'
          }`}
        >
          {cell.date.getDate()}
        </button>
        <span className="flex items-center gap-0.5 leading-none">
          {!loading && cell.hasReminder && (
            <CalMarker label="Reminder" icon={<IcBell className="w-3 h-3" />} className="text-amber-600 dark:text-amber-400" />
          )}
          {!loading && cell.hasNote && (
            <CalMarker label="Day note" icon={<IcNote className="w-3 h-3" />} className="text-casevo-mid" />
          )}
        </span>
      </div>
      {/* Chips — or, while the range loads, skeleton bars sized like chips
          (a varied 1–2 per cell so the grid doesn't read as a stripe). */}
      <div className="flex flex-col gap-1 min-w-0">
        {loading ? (
          <>
            <SkeletonBar className={`h-4 ${index % 3 === 1 ? 'w-2/3' : 'w-full'}`} />
            {index % 2 === 0 && <SkeletonBar className={`h-4 ${index % 4 === 0 ? 'w-1/2' : 'w-4/5'}`} />}
          </>
        ) : shown.map((entry, i) => (
          <Chip key={`${entry.type}-${entry.taskId ?? entry.caseId ?? ''}-${i}`} entry={entry} fade={fade} />
        ))}
        {!loading && overflow > 0 && (
          <span className="text-[10px] text-casevo-mid pl-0.5">+{overflow} more</span>
        )}
      </div>
    </div>
  )
}

// ── CHIP ───────────────────────────────────────────────────
// Task = soft green fill (status-pill family). Deadline = restrained
// red: soft red fill + a red left-border strip so it reads as the
// deliberate risk accent among the greens. Reminder = a DISTINCT amber
// fill + 🔔 glyph so a personal reminder reads apart from the firm's
// green/red/blue work items.
// A timed reminder (its date carries a real time) shows that time.
const isTimedReminderDate = (iso: string): boolean => {
  const d = new Date(iso)
  return d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0
}
const Chip: React.FC<{ entry: CalendarEntry; fade: CalendarFade }> = ({ entry, fade }) => {
  const isDeadline = entry.type === 'case_deadline'
  const isAppointment = entry.type === 'appointment'
  const isReminder = entry.type === 'reminder'
  const isOutlook = entry.type === 'outlook'
  const isCourt = isAppointment && entry.apptType === 'court'
  // Deadline red is one non-green accent; appointments a DISTINCT blue
  // (court = stronger filled indigo); reminders a DISTINCT amber; Outlook a
  // DISTINCT violet (read-only external events). Tasks ride casevo-mint.
  const cls = isCourt
    ? 'bg-indigo-100 text-indigo-900 border-l-2 border-indigo-600 dark:bg-indigo-500/25 dark:text-indigo-100 dark:border-indigo-400'
    : isOutlook
      ? 'bg-violet-100 text-violet-900 border-l-2 border-violet-500 dark:bg-violet-500/25 dark:text-violet-100 dark:border-violet-400'
      : isAppointment
        ? 'bg-sky-100 text-sky-900 border-l-2 border-sky-500 dark:bg-sky-500/25 dark:text-sky-100 dark:border-sky-400'
        : isReminder
          ? 'bg-amber-100 text-amber-950 border-l-2 border-amber-600 dark:bg-amber-500/25 dark:text-amber-100 dark:border-amber-400'
          : isDeadline && entry.status === 'completed'
            // ★ (Limitation audit) a COMPLETED deadline rendered as a live red
            // one — now muted + struck through + "Done".
            ? 'bg-casevo-input text-casevo-text-soft line-through border-l-2 border-casevo-border'
          : isDeadline
            ? 'bg-red-50 text-red-700 border-l-2 border-red-400 dark:bg-red-500/20 dark:text-red-300 dark:border-red-400/80'
            : 'bg-casevo-mint text-casevo-forest border-l-2 border-casevo-light'
  // Appointments (always) + timed reminders + timed Outlook events show time.
  const showsTime = isAppointment || (isReminder && isTimedReminderDate(entry.date)) || (isOutlook && !entry.isAllDay)
  const timePrefix = showsTime
    ? `${new Date(entry.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })} `
    : ''
  // ★ Live assignee name by id (central firmUsers; rename updates without a
  // refetch) — the server's assigneeName string is the fallback.
  const liveName = useLiveUserName()
  const assignee = entry.assigneeName ? liveName(entry.assigneeId, entry.assigneeName) : null
  const base = assignee ? `${entry.title} · ${assignee}` : entry.title
  // ↻ marks a recurring appointment occurrence. Lead / audience indicators are
  // CLEAN LINE ICONS (no emojis): reminder (bell) / Outlook (calendar) lead the
  // chip; firm-wide (globe) / attendee-count (people) trail it.
  const recurMark = isAppointment && entry.recurring ? '↻ ' : ''
  const textLabel = `${timePrefix}${recurMark}${base}`
  const audienceText = isAppointment
    ? entry.firmWide ? ' · Firm-wide' : entry.attendeeCount ? ` · ${entry.attendeeCount} attendees` : ''
    : ''
  const leadText = isReminder ? 'Reminder · ' : isOutlook ? 'Outlook · ' : isDeadline && entry.status === 'completed' ? 'Done · ' : ''
  const titleText = isOutlook
    ? `${leadText}${textLabel}${audienceText} · From Outlook`
    : entry.caseLabel ? `${leadText}${textLabel}${audienceText} — ${entry.caseLabel}` : `${leadText}${textLabel}${audienceText}`
  const chipIc = 'inline-block w-3 h-3 align-[-2px] shrink-0'
  return (
    <span
      className={`block truncate rounded-[3px] px-1.5 py-0.5 text-[10px] sm:text-[11px] leading-tight ${cls}${
        // A freshly revealed range fades in as one; a late Outlook event fades
        // in on its own (appended after the others, so nothing moves). A range
        // shown from the cache doesn't animate. reduced-motion → none.
        fadeClassFor(fade, isOutlook)
      }`}
      title={titleText}
    >
      {isReminder && (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${chipIc} mr-0.5`} aria-hidden="true"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
      )}
      {isOutlook && (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${chipIc} mr-0.5`} aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>
      )}
      {textLabel}
      {isAppointment && entry.firmWide && (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${chipIc} ml-1`} aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></svg>
      )}
      {isAppointment && !entry.firmWide && !!entry.attendeeCount && entry.attendeeCount > 0 && (
        <>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${chipIc} ml-1`} aria-hidden="true"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /></svg>
          {entry.attendeeCount}
        </>
      )}
    </span>
  )
}

export default CalendarMonthGrid
